import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import FormAlert from "../../../components/FormAlert";
import WarehouseTransferAddBill from "./WarehouseTransferAddBill";
import BillListTable from "../../../components/BillListTable";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import SalesmanSelectingPopup from "../../../components/SalesmanSelectingPopup";
import Ex_ProductSelectingPopup from "../../../components/Ex_ProductSelectingPopup";
import {
  getWarehouseTransferBills,
  executeWarehouseTransfer,
  deleteWarehouseTransferBill,
} from "../../../services/WarehouseTransfer";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const billStatus = [
  { value: "", label: "All Status" },
  { value: "DRAFT", label: "Draft" },
  { value: "AWAITING", label: "Awaiting Approval" },
  { value: "APPROVED", label: "Approved" },
  { value: "TRANSFERRED", label: "Transferred" },
  { value: "RECEIVED", label: "Received" },
  { value: "REJECTED", label: "Rejected" },
];

let _warehouseTransferBillListState = null;

function WarehouseTransferBills() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Selected products and salesman
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [selectedSalesman, setSelectedSalesman] = useState([]);

  // Popup states
  const [openProductSelection, setOpenProductSelection] = useState(false);
  const [openSalesPersonSelection, setOpenSalesPersonSelection] =
    useState(false);
  const [openPopup, setOpenPopup] = useState(false);

  // Confirmation state for delete action
  const [isMatched, setIsMatched] = useState(false);
  const [matchingText, setMachingText] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [deletingRow, setDeletingRow] = useState("");

  // Alert state
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  // Filter state
  const [filter, setFilter] = useState({
    _billNumber: "",
    _products: [],
    _barcode: "",
    _addedBy: "",
    _salesman: [],
    _status: "",
  });

  // Table columns configuration
  const columns = [
    {
      id: "date",
      label: "Date",
      minWidth: 80,
    },
    {
      id: "billNumber",
      label: "Bill Number",
      minWidth: 100,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ReceiptIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "sourceWarehouse",
      label: "Source Warehouse",
      minWidth: 120,
    },
    {
      id: "destinationWarehouse",
      label: "Destination Warehouse",
      minWidth: 120,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={
            value === "DRAFT" ? "default"
              : value === "AWAITING" ? "primary"
              : value === "APPROVED" ? "info"
              : value === "TRANSFERRED" ? "warning"
              : value === "RECEIVED" ? "success"
              : value === "REJECTED" ? "error"
              : "default"
          }
          size="small"
          variant={value === "DRAFT" ? "outlined" : "filled"}
        />
      ),
    },
    {
      id: "totalProfit",
      label: "Total Profit",
      minWidth: 100,
      format: (value) => (
        <Typography fontWeight="medium" color="success.main">
          {value.toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      id: "totalAmount",
      label: "Total Amount",
      minWidth: 100,
      format: (value) => (
        <Typography fontWeight="bold">
          {value.toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </Typography>
      ),
    },
    { id: "action", label: "Actions", minWidth: 150 },
  ];

  // Create data for table
  function createData(
    date,
    billNumber,
    sourceWarehouse,
    destinationWarehouse,
    status,
    totalProfit,
    totalAmount,
    action,
    billId
  ) {
    return {
      date,
      billNumber,
      sourceWarehouse,
      destinationWarehouse,
      status,
      totalProfit,
      totalAmount,
      action,
      billId,
    };
  }

  // Initial data load
  useEffect(() => {
    if (_warehouseTransferBillListState) {
      const { page: savedPage, filter: savedFilter } = _warehouseTransferBillListState;
      _warehouseTransferBillListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Effect for deletion confirmation
  useEffect(() => {
    if (isMatched && !openPopup && deletingRow !== null) {
      handleDeleteBill();
    }
  }, [isMatched, openPopup]);

  // Delete bill function
  const handleDeleteBill = async () => {
    setLoading(true);
    try {
      let deleteResponse = await deleteWarehouseTransferBill(deletingRow.action);
      if (deleteResponse) {
        fetchData(filter, page);
        setAlertData({
          type: "success",
          message: `Bill ${deletingRow.billNumber} successfully deleted.`,
        });
      } else {
        setAlertData({
          type: "error",
          message: `Failed to delete Bill ${deletingRow.billNumber}. Something went wrong.`,
        });
      }
      setAlert(true);
    } catch (error) {
      console.error("Error deleting bill:", error);
      setAlertData({
        type: "error",
        message: "An error occurred while deleting the bill.",
      });
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data from API
  const fetchData = async (productFilter, pageNo) => {
    setLoading(true);
    try {
      let result = await getWarehouseTransferBills(
        pageNo,
        rowsPerPage,
        productFilter
      );
      let billList = result.content;

      // Set total rows and pages for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements);

      const newSet = [];
      if (billList) {
        for (let x = 0; x < billList.length; x++) {
          newSet.push(
            createData(
              billList[x].transferDate,
              billList[x].billNumber,
              billList[x].sourceWarehouseName,
              billList[x].destinationWarehouseName,
              billList[x].status,
              billList[x].totalProfit,
              billList[x].totalPrice,
              billList[x].billId,
              billList[x].billId
            )
          );
        }
      }

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching retail bill data:", error);
      setAlertData({
        type: "error",
        message: "Error loading bill data. Please try again.",
      });
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Handle action for table actions
  const handleAction = async (event, id) => {
    switch (event) {
      case "view":
        _warehouseTransferBillListState = { page, filter };
        history.push("/template/retail_bill_view/" + id);
        break;
      case "production":
        history.push("/template/retail_bill_edit/" + id);
        break;
      case "disable":
        handleDeleteConfirmation(id);
        break;
      case "saleProduct":
        handleTransferBillItems(id.billId);
        break;
      default:
        return;
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (id) => {
    setIsMatched(false);
    let filterData = rows.find((row) => row.action === id);
    setDeletingRow(filterData);
    let topicText = `Please confirm bill number to delete: ${filterData.billNumber}`;
    setConfirmationText(topicText);
    setMachingText(filterData.billNumber);
    setOpenPopup(true);
  };

  // Handle transfer bill items (execute transfer)
  const handleTransferBillItems = async (billId) => {
    setLoading(true);
    try {
      let result = await executeWarehouseTransfer(billId);
      if (result !== "Something went wrong...") {
        setAlertData({
          type: "success",
          message: "Warehouse transfer executed successfully",
        });
        fetchData(filter, page);
      } else {
        setAlertData({
          type: "error",
          message: "Failed to execute transfer. Bill must be APPROVED first.",
        });
      }
      setAlert(true);
    } catch (error) {
      console.error("Error transferring bill items:", error);
      setAlertData({
        type: "error",
        message: "Error transferring bill items",
      });
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Close alert
  const closeAlert = () => {
    setAlert(false);
    setAlertData({
      type: "",
      message: "",
    });
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _billNumber: "",
      _products: [],
      _barcode: "",
      _addedBy: "",
      _salesman: [],
      _status: "",
    });
    setSelectedProduct([]);
    setSelectedSalesman([]);
    closeAlert();
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Product selection popup handlers
  const handleOpenProductTable = () => {
    setOpenProductSelection(true);
  };

  const handleCloseProductTable = () => {
    setOpenProductSelection(false);
    setFilter({
      ...filter,
      _products: selectedProduct,
    });
  };

  // Salesman selection popup handlers
  const handleOpenSalesmanSelection = () => {
    setOpenSalesPersonSelection(true);
  };

  const handleCloseSalesmanSelection = () => {
    setOpenSalesPersonSelection(false);
    setFilter({
      ...filter,
      _salesman: selectedSalesman,
    });
  };

  // Confirmation popup handler
  const handleOpenPopup = () => {
    setOpenPopup(!openPopup);
  };

  return (
    <StyledPaper elevation={0}>
      {openForm ? (
        <WarehouseTransferAddBill setOpenForm={setOpenForm} />
      ) : (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ReceiptIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Warehouse Transfer Bills
                </Typography>
                <Typography variant="body2">
                  View and manage warehouse transfer bills
                </Typography>
              </Grid>
              <Grid item>
                <RoleBasedRender
                  allowedRoles={["WT_BILL_CREATE", "ROLE_ADMIN"]}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenForm(true)}
                  >
                    Add Bill
                  </ActionButton>
                </RoleBasedRender>
              </Grid>
            </Grid>
          </HeaderCard>

          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <TextField
              placeholder="Search by bill number..."
              variant="outlined"
              size="small"
              sx={{ width: "40%" }}
              value={filter._billNumber}
              onChange={(e) => {
                handleFilterChange("_billNumber", e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              }}
            />
            <Stack direction="row" spacing={2}>
              <Tooltip title="Toggle Filters">
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={toggleFilters}
                >
                  Filters
                </ActionButton>
              </Tooltip>
              <Chip
                label={`${totalRows} Bills`}
                variant="outlined"
                color="primary"
              />
            </Stack>
          </Box>

          {displayAlert && (
            <Box mb={3}>
              <FormAlert type={alertData.type} message={alertData.message} />
            </Box>
          )}

          {showFilters && (
            <FilterContainer>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_addedBy"
                    value={filter._addedBy}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Added By"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Status"
                    name="_status"
                    value={filter._status}
                    onChange={(event) =>
                      handleFilterChange(event.target.name, event.target.value)
                    }
                  >
                    {billStatus.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectButton
                    startIcon={<PersonIcon />}
                    onClick={handleOpenSalesmanSelection}
                    endIcon={
                      selectedSalesman.length > 0 ? (
                        <Chip size="small" label={selectedSalesman.length} />
                      ) : null
                    }
                  >
                    {selectedSalesman.length > 0
                      ? "Salesmen Selected"
                      : "Select Salesman"}
                  </SelectButton>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectButton
                    startIcon={<InventoryIcon />}
                    onClick={handleOpenProductTable}
                    endIcon={
                      selectedProduct.length > 0 ? (
                        <Chip size="small" label={selectedProduct.length} />
                      ) : null
                    }
                  >
                    {selectedProduct.length > 0
                      ? "Products Selected"
                      : "Select Products"}
                  </SelectButton>
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={resetFilters}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => fetchData(filter, page)}
                    >
                      Apply Filters
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </FilterContainer>
          )}

          <Divider sx={{ my: 2 }} />

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 5 }}>
              <Typography variant="body1" color="textSecondary">
                No retail bills found. Try adjusting your filters or add a new
                bill.
              </Typography>
            </Box>
          ) : (
            <BillListTable
              columns={columns}
              rows={rows}
              page={page}
              setPage={setPage}
              tablePagin={false}
              totalPages={totalPages}
              handleAction={handleAction}
              showActions={{
                view: "Bill view",
                production: "Add payment",
                disable: "Delete bill",
                saleProduct: "Transfer items",
              }}
              actionRoles={{
                view: ["WT_BILL_VIEW", "ROLE_ADMIN"],
                production: ["WT_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"],
                disable: ["WT_BILL_DELETE", "ROLE_ADMIN"],
                saleProduct: ["WT_ITEM_TRANSFER", "ROLE_ADMIN"],
              }}
              tableProps={{
                sx: {
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: alpha("#000", 0.04),
                  },
                },
              }}
            />
          )}

          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                count={totalPages}
                page={page + 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handleChange}
                size="medium"
              />
            </PaginationContainer>
          )}

          {/* Popups */}
          {openPopup && (
            <ConfirmationPopup
              setOpenPopup={setOpenPopup}
              openPopup={openPopup}
              handleOpenPopup={handleOpenPopup}
              matchingText={matchingText}
              message={confirmationText}
              isMatched={isMatched}
              setIsMatched={setIsMatched}
            />
          )}

          {openSalesPersonSelection && (
            <SalesmanSelectingPopup
              setOpenSalesmanTable={setOpenSalesPersonSelection}
              openSalesmanTable={openSalesPersonSelection}
              handleCloseSalesmanTable={handleCloseSalesmanSelection}
              setSelectedSalesman={setSelectedSalesman}
              selectedSalesman={selectedSalesman}
              isOneChoise={false}
            />
          )}

          {openProductSelection && (
            <Ex_ProductSelectingPopup
              setOpenProductTable={setOpenProductSelection}
              openProductTable={openProductSelection}
              handleCloseProductTable={handleCloseProductTable}
              setSelectedProduct={setSelectedProduct}
              selectedProduct={selectedProduct}
              isOneChoise={false}
            />
          )}
        </>
      )}
    </StyledPaper>
  );
}

export default WarehouseTransferBills;
